import { clamp, hash01 } from '@/core/rng';
import type { EventBus } from '@/sim/events';
import type { BandState, StructureState, StructureType, WorkSite } from './types';
import type { BuildingPractice } from '@/sim/culture';

/**
 * Autonomous construction (spec §25–§30): work sites and standing structures.
 *
 * Bands raise what their situation demands — never what a script places.
 * A settled band wants a hearth; a crowded band wants shelters; a band whose
 * stores keep hitting the ceiling wants a granary. Each need opens a WorkSite
 * near home; the 'build' activity delivers wood from reserves and labor at
 * population × hours × 0.6 — and a band building gathers nothing, which is
 * the whole opportunity cost of architecture. Completion converts the site
 * to a StructureState under the same id.
 *
 * Abandonment (spec §30): when a band re-homes farther than 800 m from
 * structures it owns, they fall unowned — one camp_abandoned marks the leaving.
 * Unowned structures decay ~1/730 per day toward 0 and are NEVER deleted:
 * condition 0 is a ruin, archaeology, not absence.
 *
 * Determinism: every method here is called from BandSystem's absolute hour
 * and day boundaries, in band-list order; site placement is a pure function of
 * the band's home, what already stands around it, the site index and the world
 * seed. No clocks, no dice, no dependence on call pattern.
 */

/** Material and labor requirements per structure type (spec §26). */
export const STRUCTURE_COST: Record<StructureType, { wood: number; labor: number }> = {
  hearth: { wood: 5, labor: 20 },
  shelter: { wood: 12, labor: 40 },
  storage: { wood: 8, labor: 30 },
};

/** Souls one shelter keeps out of the weather. */
export const SHELTER_CAPACITY = 8;
/** Food ceiling in person-days without a granary (spec §10)… */
export const BASE_FOOD_CAP_DAYS = 14;
/** …and with one standing near home (spec §28). */
export const STORAGE_FOOD_CAP_DAYS = 30;
/** Labor delivered per person per hour of the 'build' activity. */
export const BUILD_RATE = 0.6;
/** Close enough to a work site to lay hands on it. */
export const SITE_RANGE = 60;
/** A hearth warms — and reassures — within this range (spec §28). */
export const HEARTH_RANGE = 150;

/** "Staying home" means within this of home… */
const HOME_RANGE = 200;
/** …for this much accumulated settledness before a hearth feels worth it. */
const SETTLED_DAYS = 3;
/**
 * What a day spent away costs the tally. Deliberately far less than the day
 * at home earns: foragers and hunters are out most day boundaries, and a hard
 * reset meant a band could live somewhere for a year and never light a fire —
 * which is exactly what the soak showed. Coming home one day in three is
 * enough to call a place home.
 */
const AWAY_DECAY = 0.34;
/** Days the food ceiling must be hit before a granary feels worth raising. */
const FULL_DAYS = 3;
/** Re-homing farther than this from an owned structure abandons it. */
const ABANDON_RANGE = 800;
/** Structures serving a band must stand within this of its home. */
const SERVE_RANGE = 400;
/** An unowned structure loses this much condition per day (~2-year fade). */
const RUIN_DECAY = 1 / 730;
/** A kept structure is mended this much per day — half the speed of the rot. */
const MEND_RATE = 1 / 1460;
/**
 * How far from home a band looks for standing, unowned fabric to claim —
 * the camp join distance (`tools/settlement.ts` clusters at 120 m), NOT the
 * 400 m serve range: a band moves into the camp it lives on, it does not
 * vacuum the valley's ruins.
 */
const CLAIM_RANGE = 120;
/** Below this condition a structure no longer functions (but still stands). */
export const FUNCTIONAL = 0.2;
const TAU = Math.PI * 2;

/**
 * Camp layout (spec §29). A settlement is a composition, not a confetti throw.
 * Sites used to hash an angle and a 15–50 m radius, which meant eight finished
 * structures read from the air as three unrelated triangles on bare ground —
 * the band's whole biography, invisible. So the camp is laid out instead:
 *
 *   the hearth is the origin, at the home point itself;
 *   shelters take slots on a ring around it, doors on the fire;
 *   the granary stands off that ring, in a gap between two doorways.
 *
 * Placement stays a pure function of (home, what already stands nearby, site
 * index, world seed) — no accumulated layout state, nothing to save, and a
 * restored world lays its next shelter exactly where its donor would have. The
 * ring's bearing hashes the home coordinates, so the rotation belongs to the
 * ground: two camps in one world sit differently without remembering anything.
 *
 * And a settlement is more than its hub (2026-08-23). Each band feeds its OWN
 * fire — a shared ground becomes a constellation of household fires a
 * house-row apart, and a camp past five shelters lights an outer fire — while
 * the shelters themselves draw a station: most on the ring, a share out along
 * the worn ways, a share further out in the band's own quarter. The wayside
 * only exists once travel has worn a way, and the far stations answer to a
 * `buildable` veto, so sprawl follows history onto dry ground rather than
 * being thrown by a die.
 */
/** Shelters ring the fire at this radius — close enough to be one camp. */
const RING_RADIUS = 20;
/** Each further ring steps out by this much, so a big camp is a bigger camp. */
const RING_STEP = 10;
/** Doorways per ring: six, with room to walk between them. */
const RING_SLOTS = 6;
/**
 * The order the slots fill. Plain 0,1,2,… grows the camp as an arc down one
 * side; taking every other slot first means three shelters already surround
 * the fire, which is the whole point of a ring.
 */
const SLOT_ORDER = [0, 3, 1, 4, 2, 5] as const;
/** Rings tried before placement admits the camp is full and hashes a spot. */
const RINGS = 3;
/** Two frames closer than this would share a wall. Later sites skip the slot. */
const MIN_SEPARATION = 9;
/** The granary keeps to the camp's edge, beyond the living ring. */
const STORAGE_RADIUS = 33;
/**
 * How far from home to gather the neighbours a new site must not crowd.
 * Wide enough to see everything the outland station could stand beside
 * (OUT_FAR from a fire that is itself up to ~74 m off home).
 */
const CAMP_LOOK = 380;
/**
 * Settlement sprawl (2026-08-23). Measured before this existed
 * (`tools/sprawl.ts` on the shipped century): every camp had exactly ONE
 * hearth — even the 25-structure ground five bands share — spread from the
 * fire was p50 22 m with nobody past ring 0, and 4 structures island-wide
 * stood beyond 120 m. A settlement was a dot. The brief: several hearths, a
 * main hub most shelters keep to, and some built out along the worn ways or
 * further still.
 */
/** Two fires closer than this are one camp pretending to be two. */
const HEARTH_SPACING = 26;
/** Own functional shelters per fire: the second lights at 5, the third at 10. */
const SHELTERS_PER_HEARTH = 5;
/** The most fires one band tends. */
const MAX_HEARTHS = 3;
/** A wayside shelter stands 6 m to this from its chosen point on the way. */
const WAY_REACH = 13;
/** Way points considered stand between these distances from the fire… */
const WAY_NEAR = 55;
const WAY_FAR = 240;
/** …and outland homesteads between these. */
const OUT_NEAR = 120;
const OUT_FAR = 280;
/** The station mix: hub, then wayside, then outland takes what remains. */
const HUB_SHARE = 0.62;
const WAYSIDE_SHARE = 0.22;
/** The golden angle, for walks that never revisit a bearing. */
const GOLDEN = TAU * 0.381966;

/** Per-band settlement bookkeeping the outside world never reads. */
interface BandCounters {
  /** Accumulated settledness: +1 per day boundary home, less on days away. */
  settledDays: number;
  /** Separate days on which food reserves sat at the uncapped ceiling. */
  fullDays: number;
}

export class ConstructionSystem {
  private readonly structureList: StructureState[] = [];
  private readonly siteList: WorkSite[] = [];
  private readonly counters = new Map<string, BandCounters>();
  private nextId = 0;

  constructor(
    private readonly seed: number,
    private readonly events: EventBus,
    /**
     * What roof shape this band raises (§50). Injected rather than looked up:
     * construction has no business holding the culture system, and this way
     * the one thing it needs is a pure question about a band. Absent — as in
     * a probe that builds a ConstructionSystem on its own — structures simply
     * carry no shape, and the renderer falls back to the common roof.
     */
    private readonly buildingOf?: (band: BandState) => BuildingPractice,
    /**
     * Path points within range of a point — the worn ways repeated travel has
     * made. Injected the same way as `buildingOf`: the path system belongs to
     * BandSystem, and construction needs one pure question of it. Absent, no
     * shelter is ever placed wayside; the hash falls through to the hub.
     */
    private readonly pathsNear?: (
      x: number, z: number, range: number,
    ) => ReadonlyArray<{ x: number; z: number }>,
    /**
     * Whether ground can carry a structure. The hub never needed the answer —
     * homes stand on habitat-vetted ground and 85 m keeps to it — but the
     * wayside and outland stations reach far enough to find a river or the
     * shore. Injected from the world above the sim (height > 0 and not in a
     * river, by the same bicubic test the water plane uses — never the 260 m
     * land mask, which miscalls whole headlands). Absent, all ground carries.
     */
    private readonly buildable?: (x: number, z: number) => boolean,
  ) {}

  structures(): readonly StructureState[] {
    return this.structureList;
  }

  workSites(): readonly WorkSite[] {
    return this.siteList;
  }

  /** The band's open work site, if any — one project at a time keeps it legible. */
  siteFor(bandId: string): WorkSite | undefined {
    return this.siteList.find((s) => s.ownerBandId === bandId);
  }

  /** The nearest functional hearth within range of a point, if any. */
  hearthNear(x: number, z: number, range = HEARTH_RANGE): StructureState | undefined {
    let best: StructureState | undefined;
    let bestD = range;
    for (const s of this.structureList) {
      if (s.type !== 'hearth' || s.condition < FUNCTIONAL) continue;
      const d = Math.hypot(s.x - x, s.z - z);
      if (d <= bestD) { bestD = d; best = s; }
    }
    return best;
  }

  /**
   * The band's OWN functional fires within reach of home, nearest home first
   * (ties by id, so the order is a fact rather than an accident). The first
   * entry is the household's origin: shelters ring it, the granary stands off
   * it, and the outer fires are placed from it.
   */
  private ownHearths(band: BandState): StructureState[] {
    const out: StructureState[] = [];
    for (const s of this.structureList) {
      if (s.type !== 'hearth' || s.condition < FUNCTIONAL || s.ownerBandId !== band.id) continue;
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) <= HEARTH_RANGE) out.push(s);
    }
    return out.sort((a, b) =>
      (Math.hypot(a.x - band.homeX, a.z - band.homeZ) -
        Math.hypot(b.x - band.homeX, b.z - band.homeZ)) || (a.id < b.id ? -1 : 1));
  }

  /** Functional shelters this band owns near home — the camp's built size. */
  private ownShelters(band: BandState): number {
    let k = 0;
    for (const s of this.structureList) {
      if (s.type !== 'shelter' || s.ownerBandId !== band.id || s.condition < FUNCTIONAL) continue;
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) <= SERVE_RANGE) k++;
    }
    return k;
  }

  /**
   * Whether the band has already built anything that stands by this home.
   * Labour already spent here is the plainest possible statement that the
   * place is theirs — no waiting on a tally to agree.
   */
  private servesHome(band: BandState): boolean {
    return this.structureList.some((s) =>
      s.ownerBandId === band.id && s.condition >= FUNCTIONAL &&
      Math.hypot(s.x - band.homeX, s.z - band.homeZ) <= SERVE_RANGE);
  }

  /** Souls the band's functional shelters near home can hold. */
  shelterCapacity(band: BandState): number {
    let cap = 0;
    for (const s of this.structureList) {
      if (s.type !== 'shelter' || s.ownerBandId !== band.id || s.condition < FUNCTIONAL) continue;
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) > SERVE_RANGE) continue;
      cap += s.capacity ?? SHELTER_CAPACITY;
    }
    return cap;
  }

  /** Days of food the band can hold: 14, or 30 with a granary near home (spec §28). */
  foodCapDays(band: BandState): number {
    for (const s of this.structureList) {
      if (s.type !== 'storage' || s.ownerBandId !== band.id || s.condition < FUNCTIONAL) continue;
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) <= SERVE_RANGE) {
        return STORAGE_FOOD_CAP_DAYS;
      }
    }
    return BASE_FOOD_CAP_DAYS;
  }

  /**
   * One day of settlement bookkeeping and need evaluation for one band, at
   * the absolute day boundary. Opens at most one work site — the next need
   * waits its turn behind the current project.
   */
  dailyFor(band: BandState, t: number): void {
    let c = this.counters.get(band.id);
    if (!c) {
      c = { settledDays: 0, fullDays: 0 };
      this.counters.set(band.id, c);
    }
    const distHome = Math.hypot(band.x - band.homeX, band.z - band.homeZ);
    c.settledDays = distHome <= HOME_RANGE
      ? c.settledDays + 1
      : Math.max(0, c.settledDays - AWAY_DECAY);
    if (band.reserves.food >= band.population * BASE_FOOD_CAP_DAYS - 1e-9) c.fullDays += 1;

    if (this.siteFor(band.id)) return;
    const need = this.neededType(band, c);
    if (!need) return;
    // before raising anything new: is the needed thing already STANDING,
    // sound and unowned, on this ground? Moving into what the dead left
    // beats building beside it — and because the claim is need-driven and
    // at most one a day, surplus ruins still fall; only what somebody would
    // otherwise have rebuilt is kept out of the ground.
    if (this.claim(band, need, t)) return;
    this.openSite(band, need, t);
  }

  /**
   * Take over the nearest sound, unowned structure of the needed type within
   * the camp's own reach. Nearest-then-id makes the pick a fact of the world;
   * the structure keeps its weathering (mending is `daily`'s slow work) and
   * everything downstream — capacity, fires, serve ranges — reads ownership,
   * so a claim satisfies need exactly as a build would.
   */
  private claim(band: BandState, type: StructureType, t: number): boolean {
    let best: StructureState | undefined;
    let bestD = CLAIM_RANGE;
    for (const s of this.structureList) {
      if (s.type !== type || s.ownerBandId !== undefined || s.condition < FUNCTIONAL) continue;
      const d = Math.hypot(s.x - band.homeX, s.z - band.homeZ);
      if (d < bestD || (d === bestD && best && s.id < best.id)) { bestD = d; best = s; }
    }
    if (!best) return false;
    best.ownerBandId = band.id;
    this.events.emit({
      type: 'structure_claimed', time: t, x: best.x, z: best.z,
      radius: 80, magnitude: 1, salience: 0.4,
      cause: { kind: 'mortal', bandId: band.id },
      data: { structure: type },
    });
    return true;
  }

  /** The autonomous priorities, spec §27 — each a legible sentence of need. */
  private neededType(band: BandState, c: BandCounters): StructureType | undefined {
    const fires = this.ownHearths(band);
    // a hearth: they have settled here — or already built here, which settles
    // the question — and have no fire OF THEIR OWN to gather at. Another
    // band's fire on the same ground warms them (`hearthNear` still answers
    // spec §28's warmth for any fire) but it is not theirs to feed: a
    // household raises its own, which is how a shared ground becomes a
    // constellation of fires instead of one pit five bands pretend is theirs.
    // A camp without a hearth is a camp nobody can see from a distance, so
    // this need comes first.
    if (fires.length === 0 &&
        (c.settledDays >= SETTLED_DAYS || this.servesHome(band))) {
      return 'hearth';
    }
    // shelters: not enough roofs for eight in ten souls
    if (this.shelterCapacity(band) < 0.8 * band.population) return 'shelter';
    // an outer fire: the camp has outgrown what one pit gathers. This waits
    // behind shelter need — the fire follows the neighbourhood out, it does
    // not lead it.
    if (fires.length >= 1 &&
        fires.length < Math.min(MAX_HEARTHS, 1 + Math.floor(this.ownShelters(band) / SHELTERS_PER_HEARTH))) {
      return 'hearth';
    }
    // a granary: the stores have hit the ceiling three separate days
    if (c.fullDays >= FULL_DAYS && this.foodCapDays(band) === BASE_FOOD_CAP_DAYS) {
      return 'storage';
    }
    return undefined;
  }

  /**
   * Where the next structure of this type belongs in this band's camp: the
   * first free slot in the layout above. "Free" is measured against everything
   * already standing or started within sight of home — ruins included, because
   * nobody raises a wall on a collapsed one — so later shelters fill the gaps
   * in the ring rather than landing on their elders.
   *
   * Every candidate is inside SERVE_RANGE, and the hearth's are inside
   * HEARTH_RANGE, so the finished camp always counts for the band that built
   * it and always keeps smoking.
   */
  private placeSite(band: BandState, type: StructureType, n: number): { x: number; z: number } {
    const near: Array<{ x: number; z: number }> = [];
    const fires: Array<{ x: number; z: number }> = [];
    for (const s of this.structureList) {
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) > CAMP_LOOK) continue;
      near.push(s);
      if (s.type === 'hearth' && s.condition >= FUNCTIONAL) fires.push(s);
    }
    for (const s of this.siteList) {
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) > CAMP_LOOK) continue;
      near.push(s);
      // a fire someone has STARTED claims its spacing too, or two bands
      // settling the same ground in the same week site two pits side by side
      if (s.type === 'hearth') fires.push(s);
    }
    const free = (x: number, z: number): boolean =>
      this.buildable?.(x, z) !== false &&
      !near.some((p) => Math.hypot(p.x - x, p.z - z) < MIN_SEPARATION);
    const clearsFires = (x: number, z: number): boolean =>
      !fires.some((f) => Math.hypot(f.x - x, f.z - z) < HEARTH_SPACING);
    // the camp's bearing belongs to the ground it stands on
    const base = hash01(Math.round(band.homeX), Math.round(band.homeZ), this.seed) * TAU;
    const own = this.ownHearths(band);

    if (type === 'hearth') {
      if (own.length === 0) {
        // the FIRST fire is the origin of the household's place: at home, or
        // one pace off it if a cold ring already lies there. When another
        // household's fire already burns by this home — shared ground — it
        // walks out to a quarter of its own instead, because two pits within
        // a house-row are one camp pretending to be two.
        for (let k = 0; k < 12; k++) {
          const a = k < 5 ? base + (k - 1) * (TAU / 4) : base + k * GOLDEN;
          const r = k === 0 ? 0 : k < 5 ? 7 : HEARTH_SPACING + (k - 5) * 8;
          const x = band.homeX + Math.cos(a) * r;
          const z = band.homeZ + Math.sin(a) * r;
          if (free(x, z) && clearsFires(x, z)) return { x, z };
        }
        return { x: band.homeX, z: band.homeZ };
      }
      // an OUTER fire: the camp has outgrown one pit, so the next stands a
      // house-row out from the first, among the shelters it will warm
      for (let k = 0; k < 10; k++) {
        const a = hash01(n * 53 + k * 29, 0x5c41, this.seed) * TAU;
        const r = 42 + hash01(n * 59 + k * 31, 0x5c43, this.seed) * 28;
        const x = own[0].x + Math.cos(a) * r;
        const z = own[0].z + Math.sin(a) * r;
        if (free(x, z) && clearsFires(x, z)) return { x, z };
      }
      return {
        x: own[0].x + Math.cos(base) * (HEARTH_SPACING + 4),
        z: own[0].z + Math.sin(base) * (HEARTH_SPACING + 4),
      };
    }

    // everything else composes around the household's own fire (the home
    // point until the first fire stands — which, need order being what it is,
    // is only ever briefly)
    const origin = own.length > 0
      ? { x: own[0].x, z: own[0].z }
      : { x: band.homeX, z: band.homeZ };

    if (type === 'shelter') {
      // the STATION: most shelters keep to the hub ring, but a share stand
      // out along the worn ways, and a share further out still. The hash is
      // the family's temperament; the fallthrough is the world's veto — no
      // way worn yet, or no dry ground out there, and the hub keeps them.
      // Sprawl is earned by history, not granted by a die roll.
      const station = hash01(n * 71 + 3, 0x5c47, this.seed);
      if (station >= HUB_SHARE) {
        const at = station < HUB_SHARE + WAYSIDE_SHARE
          ? this.waysidePlot(band, origin, n, free)
          : this.outlandPlot(band, origin, n, free);
        if (at) return at;
      }
    }

    const ring0 = type === 'storage' ? STORAGE_RADIUS : RING_RADIUS;
    for (let ring = 0; ring < RINGS; ring++) {
      const r = ring0 + ring * RING_STEP;
      for (const slot of SLOT_ORDER) {
        // the granary sits half a slot round, in the gap between two doors;
        // odd shelter rings do the same, so nothing queues up radially
        const half = type === 'storage' ? 0.5 : (ring % 2) * 0.5;
        const a = base + ((slot + half) / RING_SLOTS) * TAU;
        // a whisper of per-site irregularity — the slot does the composing
        const ja = hash01(n * 17 + slot * 7 + ring, 0x5c37, this.seed) - 0.5;
        const jr = hash01(n * 23 + slot * 5 + ring, 0x5c39, this.seed) - 0.5;
        const aa = a + ja * 0.14, rr = r + jr * 5;
        const x = origin.x + Math.cos(aa) * rr;
        const z = origin.z + Math.sin(aa) * rr;
        if (free(x, z)) return { x, z };
      }
    }
    // every slot for three rings out is taken: an unusually large camp spills
    // past its own plan, still well inside the range that serves the band
    let x = origin.x, z = origin.z;
    for (let k = 0; k < 6; k++) {
      const a = hash01(n * 97 + k * 89 + 13, 0x5c17, this.seed) * TAU;
      const r = ring0 + RINGS * RING_STEP + hash01(n * 131 + k * 83 + 7, 0x5c31, this.seed) * 40;
      x = origin.x + Math.cos(a) * r;
      z = origin.z + Math.sin(a) * r;
      if (free(x, z)) break;
    }
    return { x, z };
  }

  /**
   * A plot along a worn way: a point on a path 55–240 m from the fire, the
   * shelter 6–13 m off it. The candidate list is sorted by distance then
   * coordinates so the pick is a fact of the world, not of iteration order.
   * Null when no way is worn within reach — young camps stay tight, and the
   * roadside houses only appear after the road does (law 4's shape: the
   * travel makes the way, the way makes the wayside).
   */
  private waysidePlot(
    band: BandState, origin: { x: number; z: number }, n: number,
    free: (x: number, z: number) => boolean,
  ): { x: number; z: number } | null {
    if (!this.pathsNear) return null;
    const cand = this.pathsNear(origin.x, origin.z, WAY_FAR)
      .map((p) => ({ x: p.x, z: p.z, d: Math.hypot(p.x - origin.x, p.z - origin.z) }))
      .filter((p) => p.d >= WAY_NEAR && p.d <= WAY_FAR)
      .sort((a, b) => (a.d - b.d) || (a.x - b.x) || (a.z - b.z));
    if (cand.length === 0) return null;
    for (let k = 0; k < 8; k++) {
      const p = cand[Math.floor(hash01(n * 37 + k * 41, 0x5c49, this.seed) * cand.length)];
      const a = hash01(n * 43 + k * 47, 0x5c4b, this.seed) * TAU;
      const r = 6 + hash01(n * 47 + k * 53, 0x5c4d, this.seed) * (WAY_REACH - 6);
      const x = p.x + Math.cos(a) * r;
      const z = p.z + Math.sin(a) * r;
      if (Math.hypot(x - band.homeX, z - band.homeZ) > SERVE_RANGE - 30) continue;
      if (free(x, z)) return { x, z };
    }
    return null;
  }

  /**
   * An outland plot: a homestead 120–280 m out, in the band's own quarter —
   * a bearing hashed from the home ground, spread ±70°, so a band's outliers
   * form a loose arm rather than a uniform scatter. Misses walk back inward
   * (×0.88 a try) and give up once they would land in the hub's country, so
   * a shore or river camp keeps its outliers on the dry side. Null hands the
   * site back to the hub ring.
   */
  private outlandPlot(
    band: BandState, origin: { x: number; z: number }, n: number,
    free: (x: number, z: number) => boolean,
  ): { x: number; z: number } | null {
    const quarter =
      hash01(Math.round(band.homeX) * 3 + 1, Math.round(band.homeZ) * 3 - 1, this.seed) * TAU;
    for (let k = 0; k < 8; k++) {
      const a = quarter + (hash01(n * 61 + k * 67, 0x5c53, this.seed) - 0.5) * 2.4;
      const r = (OUT_NEAR + hash01(n * 73 + k * 79, 0x5c57, this.seed) * (OUT_FAR - OUT_NEAR)) *
        Math.pow(0.88, k);
      if (r < OUT_NEAR * 0.8) break;
      const x = origin.x + Math.cos(a) * r;
      const z = origin.z + Math.sin(a) * r;
      if (Math.hypot(x - band.homeX, z - band.homeZ) > SERVE_RANGE - 30) continue;
      if (free(x, z)) return { x, z };
    }
    return null;
  }

  /** Open a work site in the camp's layout; see placeSite for the geometry. */
  private openSite(band: BandState, type: StructureType, t: number): void {
    const n = this.nextId++;
    const at = this.placeSite(band, type, n);
    const site: WorkSite = {
      id: `str-${n}`,
      type,
      x: at.x,
      z: at.z,
      ownerBandId: band.id,
      required: { ...STRUCTURE_COST[type] },
      delivered: { wood: 0 },
      labor: 0,
      createdAt: t,
      built: this.buildingOf?.(band),
    };
    this.siteList.push(site);
    this.events.emit({
      type: 'structure_started', time: t, x: site.x, z: site.z,
      radius: 60, magnitude: 1, salience: 0.3,
      cause: { kind: 'mortal', bandId: band.id },
      data: { structure: type },
    });
  }

  /**
   * One stretch of the 'build' activity: deliver wood from the band's
   * reserves, then labor at population × hours × 0.6. When both requirements
   * are met the site becomes a structure under the same id, condition 1.
   */
  work(
    band: BandState,
    site: WorkSite,
    hours: number,
    t: number,
    /** Hands actually on the frame. Defaults to the whole band, but a build
     *  party is a few people, not everyone — so it passes its own size. */
    hands = band.population,
  ): void {
    const wanting = site.required.wood - site.delivered.wood;
    if (wanting > 0 && band.reserves.wood > 0) {
      const given = Math.min(wanting, band.reserves.wood);
      band.reserves.wood -= given;
      site.delivered.wood += given;
    }
    site.labor += hands * hours * BUILD_RATE;
    if (site.labor >= site.required.labor && site.delivered.wood >= site.required.wood - 1e-9) {
      this.complete(site, t);
    }
  }

  private complete(site: WorkSite, t: number): void {
    const i = this.siteList.indexOf(site);
    if (i >= 0) this.siteList.splice(i, 1);
    const built: StructureState = {
      id: site.id,
      type: site.type,
      x: site.x,
      z: site.z,
      ownerBandId: site.ownerBandId,
      builtAt: t,
      condition: 1,
      // carried from the frame, not re-asked: see WorkSite.built
      built: site.built,
    };
    if (site.type === 'shelter') built.capacity = SHELTER_CAPACITY;
    this.structureList.push(built);
    this.events.emit({
      type: 'structure_completed', time: t, x: site.x, z: site.z,
      radius: 80, magnitude: 1, salience: 0.4,
      cause: { kind: 'mortal', bandId: site.ownerBandId },
      data: { structure: site.type },
    });
  }

  /**
   * The band has re-homed (spec §30). Structures left farther than 800 m fall
   * unowned — one camp_abandoned marks the leaving — and work sites that far
   * out are simply dropped: an unfinished frame is intent, not history.
   */
  onRehome(band: BandState, t: number): void {
    let ax = 0, az = 0, released = 0;
    for (const s of this.structureList) {
      if (s.ownerBandId !== band.id) continue;
      if (Math.hypot(s.x - band.homeX, s.z - band.homeZ) <= ABANDON_RANGE) continue;
      delete s.ownerBandId;
      ax += s.x; az += s.z; released++;
    }
    if (released > 0) {
      this.events.emit({
        type: 'camp_abandoned', time: t, x: ax / released, z: az / released,
        radius: 300, magnitude: released, salience: 0.55,
        cause: { kind: 'mortal', bandId: band.id },
      });
    }
    for (let i = this.siteList.length - 1; i >= 0; i--) {
      const s = this.siteList[i];
      if (s.ownerBandId === band.id &&
          Math.hypot(s.x - band.homeX, s.z - band.homeZ) > ABANDON_RANGE) {
        this.siteList.splice(i, 1);
      }
    }
  }

  /**
   * Global upkeep at the absolute day boundary: unowned structures crumble.
   *
   * `living` is the set of bands that still exist. A camp falls unowned when
   * its band walks away (`abandon`, above) — but a band can also simply *end*,
   * by dying out or by walking into another band, and neither of those paths
   * came through here. Measured on the century-old island before this existed:
   * **98 of 212 structures belonged to a band that no longer existed, and all
   * of them stood at condition 1.00 for ever**, because a name that no longer
   * matches anybody is not `undefined`. Half the island's camps were pristine
   * hide tents kept by nobody. §30 always meant to say that the roof rots when
   * there is no one left to mend it, whichever way the leaving happened.
   */
  daily(living: ReadonlySet<string>): void {
    for (const s of this.structureList) {
      if (s.ownerBandId !== undefined && !living.has(s.ownerBandId)) delete s.ownerBandId;
      if (s.ownerBandId === undefined) {
        s.condition = clamp(s.condition - RUIN_DECAY, 0, 1);
      } else if (s.condition >= FUNCTIONAL && s.condition < 1) {
        // §30's other half: the roof rots when there is no one left to mend
        // it — so a KEPT roof is mended, at half the speed of the rot. A
        // claimed 0.25 shelter stops reading as a ruin (0.3, the renderer's
        // own line) in ~5 months and stands whole again in four years.
        // Below FUNCTIONAL nobody mends: a fallen frame is quarry, not home.
        s.condition = clamp(s.condition + MEND_RATE, 0, 1);
      }
    }
  }
}
